import React, { useEffect, useMemo, useState } from "react";
import NavigationHeader from "../../components/ui/NavigationHeader";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import Icon from "../../components/AppIcon";
import ExpeditionWorkflowPanel from "./components/ExpeditionWorkflowPanel";
import IPDVModule from "./components/IPDVModule";
import OrderMessageAutomationPanel from "./components/OrderMessageAutomationPanel";
import { baseFilterOptions, formatBaseLabel } from "../../utils/baseDisplay";
import {
  deleteCommercialRoute,
  deleteDailyRecord,
  deleteFuelEntry,
  deleteFuelVehicle,
  deletePromoterRecord,
  deletePromoterStore,
  deleteSeller,
  fetchCommercialSnapshot,
  fetchCommercialEgestorOverview,
  fetchDailyCommercialSalesSummary,
  buildSswTrackingUrl,
  buildTotalExpressTrackingUrl,
  fetchDeliveryManifest,
  fetchTotalExpressTracking,
  refreshManifestTrackingData,
  resolvePromoterReplacement,
  saveCommercialRoute,
  saveDailyRecord,
  saveFuelEntry,
  saveFuelVehicle,
  saveManifestDeliveryManual,
  savePromoterRecord,
  savePromoterStore,
  saveSeller,
} from "./services/comercialApi";

const todayISO = () => new Date().toISOString().slice(0, 10);
const operationTodayISO = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(new Date());
const daysAgoISO = (days) => {
  const date = new Date(`${operationTodayISO()}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
};
const currentMonthISO = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};
const firstDayOfMonthISO = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
};
const nowHourMinute = () => {
  const date = new Date();
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};
const formatCurrency = (value) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value || 0));
const formatNumber = (value, digits = 0) => new Intl.NumberFormat("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Number(value || 0));
const formatPercent = (value) => `${formatNumber(value, 1)}%`;
const formatDateBR = (value) => {
  if (!value) return "---";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};
const formatDateTimeBR = (value) => {
  if (!value) return "---";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("pt-BR");
};
const daysBetweenDates = (from, to) => {
  if (!from || !to) return null;
  const start = new Date(`${String(from).slice(0, 10)}T00:00:00`);
  const end = new Date(`${String(to).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const diff = Math.abs(Math.round((end.getTime() - start.getTime()) / 86400000));
  return Number.isFinite(diff) ? diff : null;
};
const normalizeKey = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
const formatMonthBR = (value) => {
  if (!value) return "mês atual";
  const [year, month] = String(value).slice(0, 7).split("-");
  if (!year || !month) return value;
  return `${month}/${year}`;
};
const quantityTotal = (quantities = {}) => Object.values(quantities || {}).reduce((acc, value) => acc + Number(value || 0), 0);
const formatPackBreakdown = (fechadas, sobra, label) => `${formatNumber(fechadas)} ${label}${Number(sobra || 0) > 0 ? ` + ${formatNumber(sobra)} un` : ""}`;
const formatManifestCaixas = (row) => formatPackBreakdown(row.caixasFechadas || 0, row.unidadesSobraCaixa || 0, "cx");
const formatManifestDisplays = (row) => formatPackBreakdown(row.displaysFechados || 0, row.unidadesSobraDisplay || 0, "disp");
const formatCnpj = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length !== 14) return value || "---";
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};
const getManifestSaleKey = (row = {}) => String(row.id || `${row.base || "base"}-${row.vendaCodigo || row.codigo || "venda"}-${row.data || row.dtEntrega || row.dtVenda || "sem-data"}`);
const escapePrintText = (value) => String(value ?? "---").replace(/[&<>"']/g, (char) => ({
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  "\"": "&quot;",
  "'": "&#39;",
}[char]));

// Altere para true se quiser reexibir os atalhos das rotas legadas sem recriar as páginas.
const SHOW_LEGACY_ROUTES_SHORTCUTS = false;

const tabItems = [
  { value: "comercial", label: "Comercial", icon: "TrendingUp", description: "Vendedores, rotas, faturamento e positivação" },
  { value: "romaneio", label: "Romaneio de Entrega", icon: "PackageCheck", description: "Vendas do eGestor, produto, caixas e displays" },
  { value: "combustivel", label: "Controle de Combustível", icon: "Fuel", description: "KM final x próximo KM inicial e cobrança de gasolina" },
  { value: "promotor", label: "Promotor de Vendas", icon: "Store", description: "PDVs, roteiros, visitas, contagens e trocas por sabor" },
];


const DEFAULT_MANIFEST_STATUS = "PEDIDO OK";

const emptySnapshot = {
  sellers: [],
  commercialRoutes: [],
  dailyCommercialRecords: [],
  fuelVehicles: [],
  fuelEntries: [],
  promoterStores: [],
  promoterRecords: [],
  productCatalog: [],
  dashboards: {
    comercial: { monthKey: "", summary: {}, routeMetrics: [], sellerMetrics: [] },
    combustivel: { monthKey: "", summary: {}, monthlyByVehicle: [], dailySummaries: [], fuelChargeSummaries: [] },
    promotor: { summary: {}, storeStats: [], alerts: { lojasComTroca: [], lojasSemVisita7Dias: [], lojasSemVisita15Dias: [], lojasForaCheckout: [] } },
  },
};

const emptyManifest = {
  filters: {},
  constants: { caixaUnidades: 40, displayUnidades: 10 },
  summary: {},
  productSummary: [],
  saleSummary: [],
  rows: [],
  warnings: [],
  bases: [],
};

const initialSellerForm = {
  id: "",
  nome: "",
  metaPositivacaoMes: "",
  metaFaturamentoMes: "",
  ativo: true,
};

const initialRouteForm = {
  id: "",
  sellerId: "",
  nomeRota: "",
  quantidadeVisitas: "",
  ativo: true,
};

const initialDailyForm = {
  id: "",
  data: todayISO(),
  routeId: "",
  visitasExecutadas: "",
  faturamento: "",
  positivacao: "",
  ticketMedioManual: "",
  observacao: "",
};

const initialFuelVehicleForm = {
  id: "",
  nome: "",
  modeloMoto: "",
  placa: "",
  distanciaEmpresaCasa: "",
  distanciaCasaEmpresa: "",
  precoGasolina: "6.50",
  consumoKmPorLitro: "30",
  ativo: true,
};

const initialFuelEntryForm = {
  id: "",
  data: todayISO(),
  hora: nowHourMinute(),
  vehicleId: "",
  tipo: "inicio",
  km: "",
  observacao: "",
};

const initialManifestFilters = {
  dtIni: operationTodayISO(),
  dtFim: operationTodayISO(),
  dtTipo: "dtVenda",
  base: "ALL",
  produto: "",
  limit: "1000",
};

const initialOverviewFilters = {
  dtIni: firstDayOfMonthISO(),
  dtFim: todayISO(),
  base: "ALL",
  limit: "1000",
};

const emptyOverview = {
  filters: {},
  summary: {},
  productSummary: [],
  warnings: [],
};


const initialStoreForm = {
  id: "",
  cnpj: "",
  nomeLoja: "",
  bairro: "",
  cidade: "",
  responsavel: "",
  ativo: true,
};

const buildEmptyQuantities = (catalog = []) => Object.fromEntries((catalog || []).map((product) => [product, ""]));
const resetPromoterForm = (catalog = []) => ({
  id: "",
  data: todayISO(),
  promoterName: "",
  storeId: "",
  quantities: buildEmptyQuantities(catalog),
  observacao: "",
  teveTroca: false,
  entregaMercadoria: false,
  displayCheckout: false,
  displayCheckstand: false,
});

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-[#f7efe2] text-[#3b1d0f]">
      <NavigationHeader />
      <main className="px-4 pb-10 pt-24 sm:px-6 lg:px-8">{children}</main>
    </div>
  );
}

function Panel({ title, subtitle, icon, children, className = "" }) {
  return (
    <section className={`rounded-[24px] border border-[#ead8c2] bg-white shadow-sm ${className}`}>
      <div className="flex flex-col gap-3 border-b border-[#f2e6d7] px-4 py-4 sm:px-5 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          {icon ? (
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#fff3df] text-[#d97706]">
              <Icon name={icon} size={18} />
            </div>
          ) : null}
          <div>
            <h2 className="text-lg font-semibold text-[#3b1d0f]">{title}</h2>
            {subtitle ? <p className="text-sm text-[#7a5c46]">{subtitle}</p> : null}
          </div>
        </div>
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

function MetricCard({ label, value, helper, icon }) {
  return (
    <div className="rounded-2xl border border-[#f0ddc7] bg-[#fffaf4] p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#8b6a52]">{label}</span>
        {icon ? <Icon name={icon} size={16} /> : null}
      </div>
      <div className="mt-3 text-2xl font-bold text-[#3b1d0f]">{value}</div>
      {helper ? <div className="mt-1 text-xs text-[#8b6a52]">{helper}</div> : null}
    </div>
  );
}

function Table({ columns, rows, emptyMessage = "Sem registros.", rowKey }) {
  return (
    <div className="overflow-x-auto">
      <table className="responsive-card-table min-w-full text-sm">
        <thead>
          <tr className="border-b border-[#f2e6d7] text-left text-xs uppercase tracking-wide text-[#8b6a52]">
            {columns.map((column) => (
              <th key={column.key} className={`${column.headerClassName || ""} px-0 py-3 pr-4 font-semibold last:pr-0`}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr className="responsive-card-table-empty"><td className="py-4 text-sm text-[#8b6a52]" colSpan={columns.length}>{emptyMessage}</td></tr>
          ) : (
            rows.map((row, index) => (
              <tr key={rowKey ? rowKey(row, index) : row.id || index} className="border-b border-[#f7ede2] align-top">
                {columns.map((column) => (
                  <td key={column.key} data-label={column.label} className={`${column.className || ""} py-3 pr-4 text-[#3b1d0f] last:pr-0`}>{column.render ? column.render(row, index) : (row[column.key] ?? "---")}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}


function PaginatedTable({ columns, rows, emptyMessage = "Sem registros.", rowKey, pageSize = 6 }) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil((rows?.length || 0) / pageSize));

  useEffect(() => {
    setPage((current) => Math.min(Math.max(1, current), totalPages));
  }, [totalPages]);

  const start = (page - 1) * pageSize;
  const visibleRows = (rows || []).slice(start, start + pageSize);

  return (
    <div>
      <Table columns={columns} rows={visibleRows} emptyMessage={emptyMessage} rowKey={rowKey} />
      {(rows?.length || 0) > pageSize ? (
        <div className="mt-4 flex flex-col gap-3 border-t border-[#f2e6d7] pt-4 text-sm sm:flex-row sm:items-center sm:justify-between">
          <span className="text-[#7a5c46]">
            Mostrando {start + 1}–{Math.min(start + pageSize, rows.length)} de {rows.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#ead8c2] bg-white text-[#7a5c46] transition hover:border-[#f4a300] hover:text-[#d97706] disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Página anterior"
              title="Página anterior"
            >
              <Icon name="ChevronLeft" size={17} />
            </button>
            <span className="min-w-[88px] text-center font-semibold text-[#3b1d0f]">Página {page} de {totalPages}</span>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={page >= totalPages}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#ead8c2] bg-white text-[#7a5c46] transition hover:border-[#f4a300] hover:text-[#d97706] disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Próxima página"
              title="Próxima página"
            >
              <Icon name="ChevronRight" size={17} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SectionTabs({ activeTab, onChange }) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
      {tabItems.map((tab) => {
        const active = activeTab === tab.value;
        return (
          <button key={tab.value} type="button" onClick={() => onChange(tab.value)} className={`rounded-[24px] border px-4 py-4 text-left transition-all ${active ? "border-[#f4a300] bg-[#f4a300] text-white shadow-md" : "border-[#ead8c2] bg-white text-[#3b1d0f] hover:border-[#f4a300] hover:bg-[#fff8ef]"}`}>
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-2xl ${active ? "bg-white/20" : "bg-[#fff3df] text-[#d97706]"}`}><Icon name={tab.icon} size={18} /></div>
              <div>
                <div className="font-semibold">{tab.label}</div>
                <div className={`mt-1 text-sm ${active ? "text-white/90" : "text-[#7a5c46]"}`}>{tab.description}</div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ActionButtons({ onEdit, onDelete }) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="xs" variant="outline" onClick={onEdit}>Editar</Button>
      <Button size="xs" variant="danger" onClick={onDelete}>Excluir</Button>
    </div>
  );
}

export default function ComercialDashboardPage() {
  const [activeTab, setActiveTab] = useState("comercial");
  const [snapshot, setSnapshot] = useState(emptySnapshot);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [sellerForm, setSellerForm] = useState(initialSellerForm);
  const [routeForm, setRouteForm] = useState(initialRouteForm);
  const [dailyForm, setDailyForm] = useState(initialDailyForm);
  const [dailyAutoLoading, setDailyAutoLoading] = useState(false);
  const [overviewFilters, setOverviewFilters] = useState(initialOverviewFilters);
  const [overview, setOverview] = useState(emptyOverview);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewLoaded, setOverviewLoaded] = useState(false);
  const [overviewError, setOverviewError] = useState("");
  const [fuelVehicleForm, setFuelVehicleForm] = useState(initialFuelVehicleForm);
  const [fuelEntryForm, setFuelEntryForm] = useState(initialFuelEntryForm);
  const [fuelMonth, setFuelMonth] = useState(currentMonthISO());
  const [fuelDate, setFuelDate] = useState(todayISO());
  const [storeForm, setStoreForm] = useState(initialStoreForm);
  const [promoterForm, setPromoterForm] = useState(resetPromoterForm([]));
  const [promoterDate, setPromoterDate] = useState(todayISO());
  const [manifestFilters, setManifestFilters] = useState(initialManifestFilters);
  const [manifest, setManifest] = useState(emptyManifest);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [manifestLoaded, setManifestLoaded] = useState(false);
  const [manifestError, setManifestError] = useState("");
  const [selectedManifestSaleIds, setSelectedManifestSaleIds] = useState([]);
  const [manifestPrintQuantities, setManifestPrintQuantities] = useState({});
  const [savingManifestMetaKey, setSavingManifestMetaKey] = useState("");
  const [trackingLoadingKey, setTrackingLoadingKey] = useState("");
  const [expeditionOrdersByKey, setExpeditionOrdersByKey] = useState({});

  const loadSnapshot = async (monthKey = fuelMonth, selectedFuelDate = fuelDate, selectedPromoterDate = promoterDate) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchCommercialSnapshot({ fuelMonth: monthKey, fuelDate: selectedFuelDate, promoterDate: selectedPromoterDate });
      const next = data || emptySnapshot;
      setSnapshot(next);
      setPromoterForm((current) => ({
        ...current,
        quantities: Object.keys(current.quantities || {}).length
          ? { ...buildEmptyQuantities(next.productCatalog || []), ...current.quantities }
          : buildEmptyQuantities(next.productCatalog || []),
      }));
    } catch (err) {
      setError(err.message || "Não foi possível carregar as telas do comercial.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSnapshot(fuelMonth, fuelDate, promoterDate); }, [fuelMonth, fuelDate, promoterDate]);

  const loadManifest = async () => {
    setManifestLoading(true);
    setManifestError("");
    try {
      const data = await fetchDeliveryManifest(manifestFilters);
      setManifest(data || emptyManifest);
      setSelectedManifestSaleIds([]);
      setManifestPrintQuantities({});
      setManifestLoaded(true);
    } catch (err) {
      setManifestError(err.message || "Não foi possível buscar as vendas do eGestor.");
    } finally {
      setManifestLoading(false);
    }
  };

  const loadOverview = async () => {
    setOverviewLoading(true);
    setOverviewError("");
    try {
      const data = await fetchCommercialEgestorOverview(overviewFilters);
      setOverview(data || emptyOverview);
      setOverviewLoaded(true);
    } catch (err) {
      setOverviewError(err.message || "Não foi possível buscar a visão geral do eGestor.");
    } finally {
      setOverviewLoading(false);
    }
  };

  const loadDailyAutoSummary = async () => {
    if (!dailyForm.data) {
      window.alert("Informe a data do lançamento antes de puxar do eGestor.");
      return;
    }

    setDailyAutoLoading(true);
    setError("");
    try {
      const data = await fetchDailyCommercialSalesSummary({ data: dailyForm.data, base: "ALL" });
      const summary = data?.summary || {};
      const faturamento = Number(summary.faturamento || 0);
      const positivacao = Number(summary.positivacao || summary.quantidadePedidos || 0);
      const ticketMedio = Number(summary.ticketMedio || 0);

      setDailyForm((current) => ({
        ...current,
        faturamento: faturamento ? faturamento.toFixed(2) : "0.00",
        positivacao: String(positivacao || 0),
        ticketMedioManual: ticketMedio ? ticketMedio.toFixed(2) : "0.00",
        observacao: current.observacao || `Dados puxados do eGestor: ${summary.quantidadePedidos || 0} pedido(s) em ${formatDateBR(dailyForm.data)}.`,
      }));
    } catch (err) {
      setError(err.message || "Não foi possível puxar faturamento/positivação do eGestor.");
    } finally {
      setDailyAutoLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "romaneio" && !manifestLoaded && !manifestLoading) {
      loadManifest();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "comercial" && !overviewLoaded && !overviewLoading) {
      loadOverview();
    }
  }, [activeTab]);

  const sellerOptions = useMemo(() => snapshot.sellers.map((seller) => ({ value: seller.id, label: seller.nome })), [snapshot.sellers]);
  const routeOptions = useMemo(() => snapshot.commercialRoutes.map((route) => ({ value: route.id, label: `${route.nomeRota} · ${route.sellerNome}` })), [snapshot.commercialRoutes]);
  const fuelVehicleOptions = useMemo(() => snapshot.fuelVehicles.map((item) => ({ value: item.id, label: `${item.nome} · ${item.modeloMoto || "Sem modelo"}${item.placa ? ` · ${item.placa}` : ""}` })), [snapshot.fuelVehicles]);
  const promoterStoreOptions = useMemo(() => snapshot.promoterStores.map((item) => ({ value: item.id, label: `${item.nomeLoja} · ${item.bairro || "Sem bairro"}` })), [snapshot.promoterStores]);

  const runAction = async (handler, successReset) => {
    setSaving(true);
    setError("");
    try {
      await handler();
      if (typeof successReset === "function") successReset();
      await loadSnapshot();
    } catch (err) {
      setError(err.message || "Não foi possível salvar os dados.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fn, id) => {
    if (!window.confirm("Deseja realmente excluir este registro?")) return;
    await runAction(() => fn(id));
  };

  const handleResolveReplacement = async (store) => {
    const confirmedBy = window.prompt(`Informe quem confirmou a troca da loja ${store.nomeLoja}:`, "");
    if (confirmedBy === null) return;
    if (!confirmedBy.trim()) {
      setError("Informe quem confirmou a realização da troca.");
      return;
    }
    if (!window.confirm(`Confirmar que a troca de ${store.nomeLoja} foi realizada?`)) return;
    await runAction(() => resolvePromoterReplacement(store.storeId, { responsavel: confirmedBy.trim() }));
  };

  const comercial = snapshot.dashboards?.comercial || emptySnapshot.dashboards.comercial;
  const combustivel = snapshot.dashboards?.combustivel || emptySnapshot.dashboards.combustivel;
  const promotor = snapshot.dashboards?.promotor || emptySnapshot.dashboards.promotor;
  const promoterStoreStatsWithGap = useMemo(() => {
    const recordsById = new Map();
    const recordsByName = new Map();

    (snapshot.promoterRecords || []).forEach((record) => {
      const visitDate = String(record.data || "").slice(0, 10);
      if (!visitDate) return;

      const idKey = String(record.storeId || "");
      if (idKey) {
        const list = recordsById.get(idKey) || [];
        list.push(visitDate);
        recordsById.set(idKey, list);
      }

      const store = (snapshot.promoterStores || []).find((item) => String(item.id) === idKey);
      const nameKey = normalizeKey(record.nomeLoja || record.loja || store?.nomeLoja);
      if (nameKey) {
        const list = recordsByName.get(nameKey) || [];
        list.push(visitDate);
        recordsByName.set(nameKey, list);
      }
    });

    const getVisits = (row) => {
      const idKey = String(row.storeId || row.id || "");
      const nameKey = normalizeKey(row.nomeLoja || row.loja);
      const visits = [...(recordsById.get(idKey) || []), ...(recordsByName.get(nameKey) || [])];
      return Array.from(new Set(visits)).sort((a, b) => b.localeCompare(a));
    };

    return (promotor.storeStats || []).map((row) => {
      const visits = getVisits(row);
      const ultimaVisita = visits[0] || row.ultimaVisita || "";
      const penultimaVisita = visits[1] || "";
      const diferencaVisitasDias = penultimaVisita ? daysBetweenDates(penultimaVisita, ultimaVisita) : null;
      const diasSemVisita = ultimaVisita ? daysBetweenDates(ultimaVisita, todayISO()) : null;
      return { ...row, ultimaVisita, penultimaVisita, diferencaVisitasDias, diasSemVisita };
    });
  }, [promotor.storeStats, snapshot.promoterRecords, snapshot.promoterStores]);
  const routeMetrics = comercial.routeMetrics || [];
  const sellerMonthlyMetrics = comercial.sellerMetrics || [];
  const fuelMonthlyByVehicle = combustivel.monthlyByVehicle || [];
  const manifestSales = manifest.saleSummary || [];
  const [messageHistoryRevision, setMessageHistoryRevision] = useState(0);
  const manifestCompanies = useMemo(() => Array.from(new Map(
    manifestSales
      .filter((row) => row.remetenteCnpj)
      .map((row) => [row.base, { base: row.base, nome: row.remetenteNome || "", cnpj: row.remetenteCnpj }])
  ).values()), [manifestSales]);
  const manifestSummary = manifest.summary || {};
  const overviewSummary = overview.summary || {};
  const overviewProductSummary = overview.productSummary || [];
  const overviewSellerSummary = overview.sellerSummary || overviewSummary.vendedores || [];
  const overviewTopClients = overviewSummary.topClientes || [];
  const selectedManifestSaleSet = useMemo(() => new Set(selectedManifestSaleIds), [selectedManifestSaleIds]);
  const manifestSaleIds = useMemo(() => manifestSales.map((row) => getManifestSaleKey(row)), [manifestSales]);
  const selectedManifestSales = useMemo(
    () => manifestSales.filter((row) => selectedManifestSaleSet.has(getManifestSaleKey(row)))
      .map((row) => {
        const overrides = manifestPrintQuantities[getManifestSaleKey(row)] || {};
        return {
          ...row, ...overrides,
          caixasImpressaoManual: Object.hasOwn(overrides, "caixasFechadas"),
          displaysImpressaoManual: Object.hasOwn(overrides, "displaysFechados"),
        };
      }),
    [manifestSales, manifestPrintQuantities, selectedManifestSaleSet]
  );
  const updateManifestPrintQuantity = (orderKey, field, value) => {
    if (!["caixasFechadas", "displaysFechados"].includes(field)) return;
    setManifestPrintQuantities((current) => ({
      ...current,
      [orderKey]: { ...(current[orderKey] || {}), [field]: value === "" ? "" : Math.max(0, Math.floor(Number(value) || 0)) },
    }));
  };
  const toggleManifestSaleSelection = (row, checked) => {
    const key = getManifestSaleKey(row);
    setSelectedManifestSaleIds((current) => {
      const next = new Set(current);
      if (checked) next.add(key);
      else next.delete(key);
      return Array.from(next);
    });
  };

  const toggleAllManifestSales = (checked) => {
    setSelectedManifestSaleIds(checked ? manifestSaleIds : []);
  };

  const generateOverviewReport = () => {
    if (!overviewLoaded || overviewLoading) {
      window.alert("Aguarde os KPIs do eGestor terminarem de carregar antes de gerar o relatório.");
      return;
    }

    const reportWindow = window.open("", "_blank", "width=1500,height=900");
    if (!reportWindow) {
      window.alert("O navegador bloqueou a abertura do relatório. Libere os pop-ups desta página e tente novamente.");
      return;
    }

    const generatedAt = new Date().toLocaleString("pt-BR");
    const companyLabel = baseFilterOptions.find((option) => option.value === overviewFilters.base)?.label
      || formatBaseLabel(overviewFilters.base)
      || "Todas as empresas";
    const metricCards = [
      ["Produtos vendidos", formatNumber(overviewSummary.produtosVendidosTotal || 0), "Total do relatório ABC"],
      ["Pedidos feitos", formatNumber(overviewSummary.quantidadePedidos || 0), "Vendas no período"],
      ["Faturamento total", formatCurrency(overviewSummary.faturamentoTotal || 0), "Soma dos pedidos"],
      ["Clientes ativos", formatNumber(overviewSummary.clientesAtivos45Dias || 0), "Compraram nos últimos 45 dias"],
      ["Positivação total", formatNumber(overviewSummary.clientesPositivadosMes || 0), "Clientes que compraram no período"],
      ["Ticket médio", formatCurrency(overviewSummary.ticketMedioCompra || 0), "Faturamento / pedidos"],
    ].map(([label, value, helper]) => `
      <div class="metric-card">
        <div class="metric-label">${escapePrintText(label)}</div>
        <div class="metric-value">${escapePrintText(value)}</div>
        <div class="metric-helper">${escapePrintText(helper)}</div>
      </div>`).join("");

    const productRows = overviewProductSummary.map((row) => `
      <tr>
        <td>${escapePrintText(row.produto)}</td>
        <td class="num">${escapePrintText(formatNumber(row.quantidade))}</td>
        <td class="num">${escapePrintText(formatNumber(row.pedidos))}</td>
        <td class="num">${escapePrintText(formatCurrency(row.valorTotal))}</td>
      </tr>`).join("") || '<tr><td colspan="4" class="empty">Nenhum produto vendido no período.</td></tr>';

    const sellerRows = overviewSellerSummary.map((row) => `
      <tr>
        <td>${escapePrintText(row.vendedor)}</td>
        <td class="num">${escapePrintText(formatNumber(row.pedidos))}</td>
        <td class="num">${escapePrintText(formatNumber(row.positivacao))}</td>
        <td class="num">${escapePrintText(formatCurrency(row.faturamento))}</td>
        <td class="num">${escapePrintText(formatCurrency(row.ticketMedio))}</td>
      </tr>`).join("") || '<tr><td colspan="5" class="empty">Nenhum vendedor retornado no período.</td></tr>';

    const clientRows = overviewTopClients.map((row) => `
      <tr>
        <td>${escapePrintText(row.cliente)}</td>
        <td class="num">${escapePrintText(formatNumber(row.pedidos))}</td>
        <td class="num">${escapePrintText(formatNumber(row.positivacao))}</td>
        <td class="num">${escapePrintText(formatCurrency(row.faturamento))}</td>
        <td class="num">${escapePrintText(formatDateBR(row.ultimaCompra))}</td>
      </tr>`).join("") || '<tr><td colspan="5" class="empty">Nenhum cliente no período.</td></tr>';

    reportWindow.document.write(`<!doctype html>
      <html lang="pt-BR">
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Relatório comercial eGestor</title>
          <style>
            * { box-sizing: border-box; }
            html { background: #f7efe2; }
            body { margin: 0; padding: 18px; font-family: Arial, sans-serif; color: #3b1d0f; background: #f7efe2; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .report { max-width: 1500px; margin: 0 auto; overflow: hidden; border: 1px solid #ead8c2; border-radius: 18px; background: #fff; box-shadow: 0 2px 8px rgba(59,29,15,.08); }
            .header { display: flex; align-items: center; gap: 12px; padding: 16px 18px; border-bottom: 1px solid #f2e6d7; }
            .header-icon { width: 36px; height: 36px; display: grid; place-items: center; border-radius: 12px; color: #d97706; background: #fff3df; font-size: 20px; font-weight: 700; }
            h1 { margin: 0; font-size: 19px; }
            .subtitle { margin-top: 3px; color: #7a5c46; font-size: 11px; }
            .content { padding: 16px; }
            .filters { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
            .filter { padding: 9px 11px; border: 1px solid #f0ddc7; border-radius: 10px; background: #fffaf4; }
            .filter-label { margin-bottom: 5px; color: #8b6a52; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; }
            .filter-value { font-size: 12px; font-weight: 700; }
            .metrics { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; margin-top: 12px; }
            .metric-card { min-height: 88px; padding: 12px; border: 1px solid #f0ddc7; border-radius: 12px; background: #fffaf4; }
            .metric-label { color: #8b6a52; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; }
            .metric-value { margin-top: 9px; font-size: 19px; font-weight: 800; white-space: nowrap; }
            .metric-helper { margin-top: 4px; color: #8b6a52; font-size: 9px; }
            .tables { display: grid; grid-template-columns: 1.05fr .95fr; gap: 12px; margin-top: 12px; align-items: start; }
            .table-card { overflow: hidden; border: 1px solid #f0ddc7; border-radius: 12px; background: #fffaf4; }
            .table-heading { padding: 12px 12px 8px; }
            h2 { margin: 0; font-size: 13px; }
            .table-subtitle { margin: 4px 0 0; color: #7a5c46; font-size: 9px; }
            table { width: 100%; border-collapse: collapse; font-size: 9px; }
            thead { display: table-header-group; }
            tr { break-inside: avoid; page-break-inside: avoid; }
            th { padding: 7px 8px; color: #8b6a52; border-top: 1px solid #f2e6d7; border-bottom: 1px solid #ead8c2; text-align: left; text-transform: uppercase; font-size: 8px; letter-spacing: .02em; }
            td { padding: 7px 8px; border-bottom: 1px solid #f2e6d7; vertical-align: top; }
            tbody tr:last-child td { border-bottom: 0; }
            .num { text-align: right; white-space: nowrap; }
            .empty { padding: 18px; color: #8b6a52; text-align: center; }
            .clients { margin-top: 12px; }
            .footer { display: flex; justify-content: space-between; gap: 16px; margin-top: 12px; color: #7a5c46; font-size: 8px; }
            @page { size: A4 landscape; margin: 8mm; }
            @media print {
              html, body { background: #fff; }
              body { padding: 0; }
              .report { max-width: none; border-radius: 0; box-shadow: none; }
              .clients { break-before: auto; }
            }
          </style>
        </head>
        <body>
          <main class="report">
            <header class="header">
              <div class="header-icon">▥</div>
              <div>
                <h1>Visão geral comercial eGestor</h1>
                <div class="subtitle">KPIs automáticos de vendas, produtos, clientes e faturamento</div>
              </div>
            </header>
            <div class="content">
              <section class="filters">
                <div class="filter"><div class="filter-label">Data inicial</div><div class="filter-value">${escapePrintText(formatDateBR(overviewFilters.dtIni))}</div></div>
                <div class="filter"><div class="filter-label">Data final</div><div class="filter-value">${escapePrintText(formatDateBR(overviewFilters.dtFim))}</div></div>
                <div class="filter"><div class="filter-label">Empresa</div><div class="filter-value">${escapePrintText(companyLabel)}</div></div>
                <div class="filter"><div class="filter-label">Limite de pedidos</div><div class="filter-value">${escapePrintText(overviewFilters.limit || "---")}</div></div>
              </section>
              <section class="metrics">${metricCards}</section>
              <section class="tables">
                <article class="table-card">
                  <div class="table-heading"><h2>Qtd. de produto por sabor</h2><p class="table-subtitle">Ranking por quantidade vendida no período selecionado.</p></div>
                  <table><thead><tr><th>Produto / sabor</th><th class="num">Qtd.</th><th class="num">Pedidos</th><th class="num">Faturamento</th></tr></thead><tbody>${productRows}</tbody></table>
                </article>
                <article class="table-card">
                  <div class="table-heading"><h2>Pedidos e faturamento por vendedor</h2><p class="table-subtitle">Resumo por vendedor do eGestor.</p></div>
                  <table><thead><tr><th>Vendedor</th><th class="num">Pedidos</th><th class="num">Positivação</th><th class="num">Faturamento</th><th class="num">Ticket</th></tr></thead><tbody>${sellerRows}</tbody></table>
                </article>
              </section>
              <article class="table-card clients">
                <div class="table-heading"><h2>Top 15 clientes por faturamento</h2><p class="table-subtitle">Ranking sem pedidos cancelados.</p></div>
                <table><thead><tr><th>Cliente</th><th class="num">Pedidos</th><th class="num">Positivação</th><th class="num">Faturamento</th><th class="num">Última compra</th></tr></thead><tbody>${clientRows}</tbody></table>
              </article>
              <footer class="footer"><span>Fonte: eGestor · ABC de produtos vendidos e vendas do período</span><span>Gerado em ${escapePrintText(generatedAt)}</span></footer>
            </div>
          </main>
        </body>
      </html>`);
    reportWindow.document.close();
    reportWindow.focus();
    setTimeout(() => reportWindow.print(), 300);
  };


  const openSswManifestTracking = (row) => {
    if (!row.nf) {
      window.alert("Essa venda ainda não retornou NF para abrir o rastreio SSW.");
      return;
    }
    if (!row.remetenteCnpj) {
      window.alert("O eGestor ainda não retornou o CNPJ da empresa remetente desta venda.");
      return;
    }
    window.open(buildSswTrackingUrl({ nf: row.nf, cnpj: row.remetenteCnpj }), "_blank", "noopener,noreferrer");
  };


  const updateManifestSaleLocal = (saleKey, patch = {}) => {
    setManifest((current) => ({
      ...current,
      saleSummary: (current.saleSummary || []).map((row) => getManifestSaleKey(row) === saleKey ? { ...row, ...patch } : row),
    }));
  };

  const saveManifestManualField = async (row, patch = {}) => {
    const saleKey = getManifestSaleKey(row);
    updateManifestSaleLocal(saleKey, patch);
    setSavingManifestMetaKey(saleKey);
    setManifestError("");
    try {
      const saved = await saveManifestDeliveryManual({
        saleKey,
        base: row.base,
        vendaCodigo: row.vendaCodigo,
        nf: patch.nf ?? row.nf,
        motorista: patch.motorista ?? row.motorista,
        statusEntrega: patch.statusEntrega ?? row.statusEntrega,
        rastreioCodigo: patch.rastreioCodigo ?? row.rastreioCodigo,
        rastreioTransportadora: patch.rastreioTransportadora ?? row.rastreioTransportadora,
        rastreioLink: patch.rastreioLink ?? row.rastreioLink,
        rastreioStatus: patch.rastreioStatus ?? row.rastreioStatus,
      });
      updateManifestSaleLocal(saleKey, saved || patch);
    } catch (err) {
      setManifestError(err.message || "Não foi possível salvar NF/motorista/status do romaneio.");
    } finally {
      setSavingManifestMetaKey("");
    }
  };

  const refreshManifestTracking = async (row) => {
    const saleKey = getManifestSaleKey(row);
    setTrackingLoadingKey(saleKey);
    setManifestError("");
    try {
      const data = await refreshManifestTrackingData({ base: row.base, vendaCodigo: row.vendaCodigo });
      const patch = {
        nf: data?.nf ?? row.nf,
        chaveNfe: data?.chaveNfe ?? row.chaveNfe,
        serieNf: data?.serieNf ?? row.serieNf,
        nfTipo: data?.nfTipo ?? row.nfTipo,
        nfSource: data?.nfSource ?? row.nfSource,
        nfAmbiente: data?.nfAmbiente ?? row.nfAmbiente,
        remetenteCnpj: data?.remetenteCnpj ?? row.remetenteCnpj,
        remetenteCnpjSource: data?.remetenteCnpjSource ?? row.remetenteCnpjSource,
        remetenteNome: data?.remetenteNome ?? row.remetenteNome,
        rastreioCodigo: data?.rastreioCodigo || row.rastreioCodigo,
        rastreioTransportadora: data?.rastreioTransportadora || row.rastreioTransportadora,
        rastreioLink: data?.rastreioLink || row.rastreioLink,
        rastreioStatus: data?.rastreioCodigo ? "Código localizado no eGestor" : "Código ainda não informado pelo eGestor",
      };
      updateManifestSaleLocal(saleKey, patch);
      await saveManifestManualField({ ...row, ...patch }, patch);
      if (!patch.rastreioCodigo) window.alert(data?.message || "O eGestor ainda não retornou código de rastreio para essa venda.");
    } catch (err) {
      setManifestError(err.message || "Não foi possível atualizar os dados de rastreio desta venda.");
    } finally {
      setTrackingLoadingKey("");
    }
  };

  const consultTotalExpressTracking = async (row) => {
    const saleKey = getManifestSaleKey(row);
    const trackingCode = String(row.rastreioCodigo || "").trim();
    if (!trackingCode) {
      window.alert("O eGestor ainda não retornou o código de rastreio desta venda. Clique em Atualizar código primeiro.");
      return;
    }

    const popup = window.open("about:blank", "_blank");
    if (popup) {
      popup.document.write("<title>Consultando Total Express</title><p style='font-family:Arial;padding:24px'>Consultando a encomenda na Total Express...</p>");
    }
    setTrackingLoadingKey(saleKey);
    setManifestError("");
    try {
      const data = await fetchTotalExpressTracking(trackingCode);
      const link = data?.link || buildTotalExpressTrackingUrl(trackingCode);
      await saveManifestManualField(row, {
        rastreioCodigo: trackingCode,
        rastreioTransportadora: row.rastreioTransportadora || "TOTAL EXPRESS",
        rastreioLink: link,
        rastreioStatus: data?.status || data?.message || "Consulta realizada na Total Express",
      });
      if (popup) popup.location.href = link;
      else window.open(link, "_blank", "noopener,noreferrer");
    } catch (err) {
      popup?.close();
      setManifestError(err.message || "Não foi possível consultar a Total Express.");
    } finally {
      setTrackingLoadingKey("");
    }
  };

  const printSelectedManifestSales = () => {
    if (!selectedManifestSales.length) {
      window.alert("Selecione pelo menos uma venda para imprimir.");
      return;
    }
    if (selectedManifestSales.some((row) => row.caixasFechadas === "" || row.displaysFechados === "")) {
      window.alert("Preencha a quantidade de caixas e displays dos pedidos selecionados. Digite 0 quando não houver.");
      return;
    }

    const totalUnidades = selectedManifestSales.reduce((acc, row) => acc + Number(row.quantidade || 0), 0);
    const totalCaixasFechadas = selectedManifestSales.reduce((acc, row) => acc + Number(row.caixasFechadas || 0), 0);
    const totalDisplaysFechados = selectedManifestSales.reduce((acc, row) => acc + Number(row.displaysFechados || 0), 0);
    const generatedAt = new Date().toLocaleString("pt-BR");
    const rowsHtml = selectedManifestSales.map((row) => {
      const link = row.rastreioLink || row.trackingLink || (row.nf && row.remetenteCnpj ? buildSswTrackingUrl({ nf: row.nf, cnpj: row.remetenteCnpj }) : "");
      return `
      <tr>
        <td>${escapePrintText(row.nf || "---")}</td>
        <td>${escapePrintText(formatCnpj(row.remetenteCnpj))}</td>
        <td>${escapePrintText(row.vendaCodigo || row.codigo)}</td>
        <td>${escapePrintText(formatDateBR(row.dataEntradaPedido || row.dtVenda))}</td>
        <td>${escapePrintText(formatDateBR(row.previsaoEntrega || row.dtEntrega || row.data))}</td>
        <td>${escapePrintText(row.cliente)}</td>
        <td class="num">${escapePrintText(row.displaysImpressaoManual ? `${row.displaysFechados} disp` : formatManifestDisplays(row))}</td>
        <td class="num">${escapePrintText(row.caixasImpressaoManual ? `${row.caixasFechadas} cx` : formatManifestCaixas(row))}</td>
        <td>${escapePrintText(row.motorista || "---")}</td>
        <td>${escapePrintText(row.statusEntrega || DEFAULT_MANIFEST_STATUS)}</td>
        <td>${escapePrintText(row.rastreioCodigo || "---")}</td>
        <td>${link ? `<a href="${escapePrintText(link)}" target="_blank" rel="noopener noreferrer">Abrir rastreio</a>` : "---"}</td>
      </tr>`;
    }).join("");

    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      window.alert("O navegador bloqueou a impressão. Libere pop-ups para este sistema e tente novamente.");
      return;
    }

    printWindow.document.write(`<!doctype html>
      <html lang="pt-BR">
        <head>
          <meta charset="utf-8" />
          <title>Romaneio de entrega</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; color: #2d1b10; margin: 24px; }
            h1 { margin: 0; font-size: 22px; }
            .subtitle { margin: 6px 0 18px; color: #6b4f3d; font-size: 13px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th { background: #fff3df; color: #6b4f3d; text-transform: uppercase; font-size: 11px; text-align: left; }
            th, td { border: 1px solid #ead8c2; padding: 8px; vertical-align: top; }
            .num { text-align: right; white-space: nowrap; }
            tfoot td { font-weight: 700; background: #fffaf4; }
            .footer { margin-top: 16px; font-size: 11px; color: #7a5c46; }
            @page { size: A4 landscape; margin: 12mm; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <h1>Romaneio de entrega</h1>
          <div class="subtitle">Vendas selecionadas para impressão · Gerado em ${escapePrintText(generatedAt)}</div>
          <table>
            <thead>
              <tr>
                <th>NF</th>
                <th>CNPJ remetente</th>
                <th>Nº pedido</th>
                <th>Entrada</th>
                <th>Previsão</th>
                <th>Nome</th>
                <th>Qtd display</th>
                <th>Qtd caixa</th>
                <th>Motorista</th>
                <th>Status</th>
                <th>Código de rastreio</th>
                <th>Link de rastreio</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
            <tfoot>
              <tr>
                <td colspan="6">Total selecionado</td>
                <td class="num">${escapePrintText(formatNumber(totalDisplaysFechados))} displays fechados</td>
                <td class="num">${escapePrintText(formatNumber(totalCaixasFechadas))} caixas fechadas</td>
                <td colspan="4" class="num">${escapePrintText(formatNumber(totalUnidades))} unidades</td>
              </tr>
            </tfoot>
          </table>
          <div class="footer">Caixa = ${escapePrintText(manifest.constants?.caixaUnidades || 40)} un · Display = ${escapePrintText(manifest.constants?.displayUnidades || 10)} un</div>
        </body>
      </html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  if (loading) {
    return <PageShell><div className="flex min-h-[50vh] items-center justify-center text-[#7a5c46]">Carregando módulo comercial...</div></PageShell>;
  }

  return (
    <PageShell>
      <div className="mx-auto flex max-w-[1600px] flex-col gap-6">
        <div className="rounded-[28px] border border-[#ead8c2] bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#3b1d0f]">Módulo Comercial</h1>
              <p className="mt-2 text-sm text-[#7a5c46]">Vendedores, rotas, combustível, promotores, metas e análises em um só lugar.</p>
            </div>
            <Button variant="outline" onClick={loadSnapshot} iconName="RefreshCw">Atualizar tudo</Button>
          </div>
        </div>

        {error ? <div className="rounded-2xl border border-[#f8c9c9] bg-[#fff1f1] px-4 py-3 text-sm text-[#b42318]">{error}</div> : null}
        <SectionTabs activeTab={activeTab} onChange={setActiveTab} />

        {activeTab === "comercial" ? (
          <div className="flex flex-col gap-6">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
              <MetricCard label="Vendedores" value={formatNumber(comercial.summary?.vendedoresAtivos || 0)} helper="Ativos" icon="Users" />
              <MetricCard label="Rotas" value={formatNumber(comercial.summary?.rotasAtivas || 0)} helper="Vinculadas aos vendedores" icon="Route" />
              <MetricCard label="Visitas planejadas" value={formatNumber(comercial.summary?.visitasPlanejadas || 0)} helper="Rotas configuradas" icon="MapPinned" />
              <MetricCard label="Visitas realizadas" value={formatNumber(comercial.summary?.visitasExecutadas || 0)} helper={`Competência ${comercial.monthKey || "atual"}`} icon="ClipboardCheck" />
              <MetricCard label="Faturamento" value={formatCurrency(comercial.summary?.faturamento || 0)} helper={`Meta ${formatCurrency(comercial.summary?.metaFaturamento || 0)}`} icon="Wallet" />
              <MetricCard label="Ticket médio" value={formatCurrency(comercial.summary?.ticketMedio || 0)} helper="Faturamento / positivação" icon="BadgeDollarSign" />
            </div>

            <Panel title="Visão geral comercial eGestor" subtitle="KPIs automáticos de vendas, produtos, clientes e faturamento" icon="BarChart3">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
                <Input label="Data inicial" type="date" value={overviewFilters.dtIni} onChange={(e) => setOverviewFilters((p) => ({ ...p, dtIni: e.target.value }))} />
                <Input label="Data final" type="date" value={overviewFilters.dtFim} onChange={(e) => setOverviewFilters((p) => ({ ...p, dtFim: e.target.value }))} />
                <Select label="Empresa" value={overviewFilters.base} onChange={(value) => setOverviewFilters((p) => ({ ...p, base: value }))} options={baseFilterOptions} />
                <Input label="Limite de pedidos" type="number" value={overviewFilters.limit} onChange={(e) => setOverviewFilters((p) => ({ ...p, limit: e.target.value }))} />
                <div className="flex items-end">
                  <Button className="w-full" loading={overviewLoading} onClick={loadOverview} iconName="RefreshCw">Atualizar KPIs</Button>
                </div>
                <div className="flex items-end">
                  <Button className="w-full" variant="outline" disabled={!overviewLoaded || overviewLoading} onClick={generateOverviewReport} iconName="FileDown">Gerar relatório</Button>
                </div>
              </div>

              {overviewError ? <div className="mt-4 rounded-2xl border border-[#f8c9c9] bg-[#fff1f1] px-4 py-3 text-sm text-[#b42318]">{overviewError}</div> : null}
              <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-6">
                <MetricCard label="Produtos vendidos" value={formatNumber(overviewSummary.produtosVendidosTotal || 0)} helper="Total do relatório ABC" icon="Boxes" />
                <MetricCard label="Pedidos feitos" value={formatNumber(overviewSummary.quantidadePedidos || 0)} helper="Vendas no período" icon="ShoppingCart" />
                <MetricCard label="Faturamento total" value={formatCurrency(overviewSummary.faturamentoTotal || 0)} helper="Soma dos pedidos" icon="Wallet" />
                <MetricCard label="Clientes ativos" value={formatNumber(overviewSummary.clientesAtivos45Dias || 0)} helper="Compraram nos últimos 45 dias" icon="UserCheck" />
                <MetricCard label="Positivação total" value={formatNumber(overviewSummary.clientesPositivadosMes || 0)} helper="Clientes que compraram no período" icon="Users" />
                <MetricCard label="Ticket médio" value={formatCurrency(overviewSummary.ticketMedioCompra || 0)} helper="Faturamento / pedidos" icon="BadgeDollarSign" />
              </div>

              <div className="mt-5 grid gap-6 xl:grid-cols-3">
                <div className="rounded-2xl border border-[#f0ddc7] bg-[#fffaf4] p-4">
                  <h3 className="mb-3 font-semibold text-[#3b1d0f]">Qtd. de produto por sabor</h3>
                  <p className="mb-3 text-xs text-[#7a5c46]">Fonte: eGestor → Relatórios → Vendas → ABC de produtos vendidos. Valor base: quantidade.</p>
                  <div className="max-h-[360px] overflow-y-auto pr-2">
                    <Table rows={overviewProductSummary} columns={[
                      { key: "produto", label: "Produto/sabor" },
                      { key: "quantidade", label: "Qtd.", render: (row) => formatNumber(row.quantidade) },
                      { key: "pedidos", label: "Pedidos", render: (row) => formatNumber(row.pedidos) },
                      { key: "valorTotal", label: "Faturamento", render: (row) => formatCurrency(row.valorTotal) },
                    ]} emptyMessage={overviewLoading ? "Buscando produtos no eGestor..." : "Nenhum produto vendido no período."} />
                  </div>
                </div>
                <div className="rounded-2xl border border-[#f0ddc7] bg-[#fffaf4] p-4">
                  <h3 className="mb-3 font-semibold text-[#3b1d0f]">Pedidos e faturamento por vendedor</h3>
                  <p className="mb-3 text-xs text-[#7a5c46]">Resumo puxando o vendedor do eGestor quando vier no pedido ou no cadastro.</p>
                  <div className="max-h-[360px] overflow-y-auto pr-2">
                    <Table rows={overviewSellerSummary} columns={[
                      { key: "vendedor", label: "Vendedor" },
                      { key: "pedidos", label: "Pedidos", render: (row) => formatNumber(row.pedidos) },
                      { key: "positivacao", label: "Positivação", render: (row) => formatNumber(row.positivacao) },
                      { key: "faturamento", label: "Faturamento", render: (row) => formatCurrency(row.faturamento) },
                      { key: "ticketMedio", label: "Ticket", render: (row) => formatCurrency(row.ticketMedio) },
                    ]} emptyMessage={overviewLoading ? "Buscando vendedores no eGestor..." : "Nenhum vendedor retornado no período."} />
                  </div>
                </div>
                <div className="rounded-2xl border border-[#f0ddc7] bg-[#fffaf4] p-4">
                  <h3 className="mb-3 font-semibold text-[#3b1d0f]">Top 15 clientes por faturamento</h3>
                  <p className="mb-3 text-xs text-[#7a5c46]">Ranking sem pedidos cancelados.</p>
                  <div className="max-h-[360px] overflow-y-auto pr-2">
                    <Table rows={overviewTopClients} columns={[
                      { key: "cliente", label: "Cliente" },
                      { key: "pedidos", label: "Pedidos", render: (row) => formatNumber(row.pedidos) },
                      { key: "positivacao", label: "Positivação", render: (row) => formatNumber(row.positivacao) },
                      { key: "faturamento", label: "Faturamento", render: (row) => formatCurrency(row.faturamento) },
                      { key: "ultimaCompra", label: "Última compra", render: (row) => formatDateBR(row.ultimaCompra) },
                    ]} emptyMessage={overviewLoading ? "Buscando clientes no eGestor..." : "Nenhum cliente no período."} />
                  </div>
                </div>
              </div>
            </Panel>

            {SHOW_LEGACY_ROUTES_SHORTCUTS && <Panel title="Rotas dentro do Comercial" subtitle="Atalhos para as rotas Mercos e rotas manuais, centralizadas no módulo comercial" icon="Route">
              <div className="grid gap-4 md:grid-cols-2">
                <button type="button" onClick={() => { window.location.href = "/mercos-rotas-dashboard"; }} className="rounded-2xl border border-[#f0ddc7] bg-[#fffaf4] p-4 text-left transition hover:border-[#f4a300] hover:bg-[#fff8ef]">
                  <div className="font-semibold text-[#3b1d0f]">Rotas Mercos</div>
                  <p className="mt-1 text-sm text-[#7a5c46]">Consulta e acompanhamento das rotas vindas do Mercos.</p>
                </button>
                <button type="button" onClick={() => { window.location.href = "/mercos-rotas-planejadas"; }} className="rounded-2xl border border-[#f0ddc7] bg-[#fffaf4] p-4 text-left transition hover:border-[#f4a300] hover:bg-[#fff8ef]">
                  <div className="font-semibold text-[#3b1d0f]">Rotas manuais</div>
                  <p className="mt-1 text-sm text-[#7a5c46]">Planejamento manual, edição atual e histórico preservado.</p>
                </button>
              </div>
            </Panel>}

            <div className="grid gap-6 xl:grid-cols-4">
              <Panel title="Cadastro de vendedor" subtitle="Vendedor, meta de positivação e meta de faturamento do mês" icon="UserCog">
                <div className="grid gap-4">
                  <Input label="Vendedor" value={sellerForm.nome} onChange={(e) => setSellerForm((p) => ({ ...p, nome: e.target.value }))} />
                  <Input label="Meta positivação do mês" type="number" value={sellerForm.metaPositivacaoMes} onChange={(e) => setSellerForm((p) => ({ ...p, metaPositivacaoMes: e.target.value }))} />
                  <Input label="Meta faturamento mês" type="number" step="0.01" value={sellerForm.metaFaturamentoMes} onChange={(e) => setSellerForm((p) => ({ ...p, metaFaturamentoMes: e.target.value }))} />
                  <label className="flex items-center gap-2 text-sm text-[#5d4637]"><input type="checkbox" checked={sellerForm.ativo} onChange={(e) => setSellerForm((p) => ({ ...p, ativo: e.target.checked }))} />Cadastro ativo</label>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button loading={saving} onClick={() => runAction(() => saveSeller(sellerForm), () => setSellerForm(initialSellerForm))} iconName="Save">{sellerForm.id ? "Atualizar vendedor" : "Cadastrar vendedor"}</Button>
                  {sellerForm.id ? <Button variant="outline" onClick={() => setSellerForm(initialSellerForm)}>Cancelar</Button> : null}
                </div>
              </Panel>

              <Panel title="Cadastro de rota" subtitle="Nome da rota, quantidade de visitas e vínculo com vendedor" icon="MapPinned">
                <div className="grid gap-4">
                  <Select label="Link com o vendedor" options={sellerOptions} value={routeForm.sellerId} onChange={(value) => setRouteForm((p) => ({ ...p, sellerId: value }))} placeholder="Selecione o vendedor" />
                  <Input label="Nome da rota" value={routeForm.nomeRota} onChange={(e) => setRouteForm((p) => ({ ...p, nomeRota: e.target.value }))} />
                  <Input label="Quantidade de visitas" type="number" value={routeForm.quantidadeVisitas} onChange={(e) => setRouteForm((p) => ({ ...p, quantidadeVisitas: e.target.value }))} />
                  <label className="flex items-center gap-2 text-sm text-[#5d4637]"><input type="checkbox" checked={routeForm.ativo} onChange={(e) => setRouteForm((p) => ({ ...p, ativo: e.target.checked }))} />Rota ativa</label>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button loading={saving} onClick={() => runAction(() => saveCommercialRoute(routeForm), () => setRouteForm(initialRouteForm))} iconName="Save">{routeForm.id ? "Atualizar rota" : "Cadastrar rota"}</Button>
                  {routeForm.id ? <Button variant="outline" onClick={() => setRouteForm(initialRouteForm)}>Cancelar</Button> : null}
                </div>
              </Panel>

              <Panel title="Lançamento diário do comercial" subtitle="Visitas manuais; faturamento, positivação e ticket podem ser puxados do eGestor pela data" icon="ClipboardPlus">
                <div className="grid gap-4">
                  <Input label="Data" type="date" value={dailyForm.data} onChange={(e) => setDailyForm((p) => ({ ...p, data: e.target.value }))} />
                  <Select label="Qual rota estou executando" options={routeOptions} value={dailyForm.routeId} onChange={(value) => setDailyForm((p) => ({ ...p, routeId: value }))} placeholder="Selecione a rota" />
                  <Input label="Quantas visitas foram realizadas" type="number" value={dailyForm.visitasExecutadas} onChange={(e) => setDailyForm((p) => ({ ...p, visitasExecutadas: e.target.value }))} />
                  <Input label="Faturamento eGestor" type="number" step="0.01" value={dailyForm.faturamento} onChange={(e) => setDailyForm((p) => ({ ...p, faturamento: e.target.value }))} />
                  <Input label="Positivação eGestor" type="number" value={dailyForm.positivacao} onChange={(e) => setDailyForm((p) => ({ ...p, positivacao: e.target.value }))} />
                  <Input label="Ticket médio eGestor" type="number" step="0.01" value={dailyForm.ticketMedioManual} onChange={(e) => setDailyForm((p) => ({ ...p, ticketMedioManual: e.target.value }))} />
                  <Input label="Observação" value={dailyForm.observacao} onChange={(e) => setDailyForm((p) => ({ ...p, observacao: e.target.value }))} />
                </div>
                <div className="mt-4 rounded-2xl border border-[#f2e6d7] bg-[#fffaf4] p-3 text-sm text-[#7a5c46]">
                  Faturamento = soma dos pedidos do dia no eGestor · Positivação = quantidade/clientes dos pedidos do dia · Ticket médio = faturamento / pedidos.
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button variant="outline" loading={dailyAutoLoading} onClick={loadDailyAutoSummary} iconName="DownloadCloud">Puxar do eGestor</Button>
                  <Button loading={saving} onClick={() => runAction(() => saveDailyRecord(dailyForm), () => setDailyForm(initialDailyForm))} iconName="Save">{dailyForm.id ? "Atualizar lançamento" : "Salvar lançamento"}</Button>
                  {dailyForm.id ? <Button variant="outline" onClick={() => setDailyForm(initialDailyForm)}>Cancelar</Button> : null}
                </div>
              </Panel>
            </div>

            <Panel title="Resumo geral do mês por vendedor" subtitle={`Resultado consolidado da competência ${formatMonthBR(comercial.monthKey)} sem precisar somar a listagem manualmente`} icon="ClipboardList">
              <Table rows={sellerMonthlyMetrics} columns={[
                { key: "vendedor", label: "Vendedor" },
                { key: "rotasAtivas", label: "Rotas", render: (row) => formatNumber(row.rotasAtivas) },
                { key: "visitasPlanejadas", label: "Visitas planejadas", render: (row) => formatNumber(row.visitasPlanejadas) },
                { key: "visitasExecutadas", label: "Visitas realizadas", render: (row) => formatNumber(row.visitasExecutadas) },
                { key: "rotaCumpridaPercentual", label: "% rota", render: (row) => formatPercent(row.rotaCumpridaPercentual) },
                { key: "faturamento", label: "Faturamento", render: (row) => `${formatCurrency(row.faturamento)} / ${formatCurrency(row.metaFaturamentoMes)}` },
                { key: "faturamentoPercentual", label: "% faturamento", render: (row) => formatPercent(row.faturamentoPercentual) },
                { key: "positivacao", label: "Positivação", render: (row) => `${formatNumber(row.positivacao)} / ${formatNumber(row.metaPositivacaoMes)}` },
                { key: "positivacaoPercentual", label: "% positivação", render: (row) => formatPercent(row.positivacaoPercentual) },
                { key: "ticketMedio", label: "Ticket médio", render: (row) => formatCurrency(row.ticketMedio) },
              ]} emptyMessage="Cadastre vendedores, rotas e lançamentos para visualizar o resumo mensal." />
            </Panel>

            <Panel title="Análise comercial por rota" subtitle="O sistema cruza o lançamento diário com as metas do vendedor e a rota configurada" icon="LineChart">
              <Table rows={routeMetrics} columns={[
                { key: "vendedor", label: "Vendedor" },
                { key: "rotaNome", label: "Rota" },
                { key: "visitasPlanejadas", label: "Visitas planejadas", render: (row) => formatNumber(row.visitasPlanejadas) },
                { key: "visitasExecutadas", label: "Visitas realizadas", render: (row) => formatNumber(row.visitasExecutadas) },
                { key: "rotaCumpridaPercentual", label: "% rota", render: (row) => formatPercent(row.rotaCumpridaPercentual) },
                { key: "faturamento", label: "Faturamento", render: (row) => `${formatCurrency(row.faturamento)} / ${formatCurrency(row.metaFaturamentoMes)}` },
                { key: "faturamentoPercentual", label: "% faturamento", render: (row) => formatPercent(row.faturamentoPercentual) },
                { key: "positivacao", label: "Positivação", render: (row) => `${formatNumber(row.positivacao)} / ${formatNumber(row.metaPositivacaoMes)}` },
                { key: "positivacaoPercentual", label: "% positivação", render: (row) => formatPercent(row.positivacaoPercentual) },
                { key: "ticketMedio", label: "Ticket médio", render: (row) => formatCurrency(row.ticketMedio) },
              ]} emptyMessage="Cadastre vendedores, rotas e lançamentos para visualizar as análises." />
            </Panel>

            <div className="grid gap-6 xl:grid-cols-4">
              <Panel title="Vendedores cadastrados" subtitle="Metas mensais" icon="Users">
                <Table rows={snapshot.sellers || []} columns={[
                  { key: "nome", label: "Vendedor" },
                  { key: "metaPositivacaoMes", label: "Meta positivação", render: (row) => formatNumber(row.metaPositivacaoMes) },
                  { key: "metaFaturamentoMes", label: "Meta faturamento", render: (row) => formatCurrency(row.metaFaturamentoMes) },
                  { key: "ativo", label: "Status", render: (row) => row.ativo ? "Ativo" : "Inativo" },
                  { key: "acoes", label: "Ações", render: (row) => <ActionButtons onEdit={() => setSellerForm({ ...initialSellerForm, ...row })} onDelete={() => handleDelete(deleteSeller, row.id)} /> },
                ]} />
              </Panel>

              <Panel title="Rotas cadastradas" subtitle="Rotas vinculadas aos vendedores" icon="Map">
                <Table rows={snapshot.commercialRoutes || []} columns={[
                  { key: "nomeRota", label: "Rota" },
                  { key: "sellerNome", label: "Vendedor" },
                  { key: "quantidadeVisitas", label: "Qtd. visitas", render: (row) => formatNumber(row.quantidadeVisitas) },
                  { key: "ativo", label: "Status", render: (row) => row.ativo ? "Ativa" : "Inativa" },
                  { key: "acoes", label: "Ações", render: (row) => <ActionButtons onEdit={() => setRouteForm({ ...initialRouteForm, ...row })} onDelete={() => handleDelete(deleteCommercialRoute, row.id)} /> },
                ]} />
              </Panel>

              <Panel title="Lançamentos comerciais" subtitle="Histórico diário" icon="History">
                <Table rows={snapshot.dailyCommercialRecords || []} columns={[
                  { key: "data", label: "Data", render: (row) => formatDateBR(row.data) },
                  { key: "rotaNome", label: "Rota" },
                  { key: "sellerNome", label: "Vendedor" },
                  { key: "visitasExecutadas", label: "Visitas" },
                  { key: "faturamento", label: "Faturamento", render: (row) => formatCurrency(row.faturamento) },
                  { key: "positivacao", label: "Pos." },
                  { key: "ticketMedio", label: "Ticket", render: (row) => formatCurrency(row.ticketMedio) },
                  { key: "acoes", label: "Ações", render: (row) => <ActionButtons onEdit={() => setDailyForm({ ...initialDailyForm, ...row, ticketMedioManual: row.ticketMedio })} onDelete={() => handleDelete(deleteDailyRecord, row.id)} /> },
                ]} />
              </Panel>
            </div>
          </div>
        ) : null}

        {activeTab === "romaneio" ? (
          <div className="flex flex-col gap-6">
            <Panel title="Romaneio de entrega" subtitle="Lista as vendas do eGestor e calcula caixas/displays pelo produto do pedido" icon="PackageCheck">
              <div className="grid gap-4 lg:grid-cols-6">
                <Input label="Data inicial" type="date" value={manifestFilters.dtIni} onChange={(e) => setManifestFilters((p) => ({ ...p, dtIni: e.target.value }))} />
                <Input label="Data final" type="date" value={manifestFilters.dtFim} onChange={(e) => setManifestFilters((p) => ({ ...p, dtFim: e.target.value }))} />
                <Select label="Filtrar por" value={manifestFilters.dtTipo} onChange={(value) => setManifestFilters((p) => ({ ...p, dtTipo: value }))} options={[{ value: "dtEntrega", label: "Data de entrega" }, { value: "dtVenda", label: "Data da venda" }, { value: "dtCad", label: "Data de cadastro" }]} />
                <Select label="Empresa" value={manifestFilters.base} onChange={(value) => setManifestFilters((p) => ({ ...p, base: value }))} options={baseFilterOptions} />
                <Input label="Produto específico" placeholder="Ex: brownie, avelã..." value={manifestFilters.produto} onChange={(e) => setManifestFilters((p) => ({ ...p, produto: e.target.value }))} />
                <Input label="Limite por empresa" type="number" value={manifestFilters.limit} onChange={(e) => setManifestFilters((p) => ({ ...p, limit: e.target.value }))} />
              </div>

              <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-[#f2e6d7] bg-[#fffaf4] p-4 text-sm text-[#7a5c46] lg:flex-row lg:items-center lg:justify-between">
                <div>
                  Caixa = <b className="text-[#3b1d0f]">{manifest.constants?.caixaUnidades || 40} un</b> · Display = <b className="text-[#3b1d0f]">{manifest.constants?.displayUnidades || 10} un</b>. Padrão: pedidos dos últimos 45 dias pela data da venda, sem filtrar o cadastro de clientes. Caixas e displays podem ser ajustados para impressão; não alteram o eGestor ou os totais da carga.
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button loading={manifestLoading} onClick={loadManifest} iconName="Search">Buscar vendas</Button>
                  <Button variant="outline" onClick={() => { setManifestFilters(initialManifestFilters); setManifest(emptyManifest); setSelectedManifestSaleIds([]); setManifestPrintQuantities({}); setManifestLoaded(false); }} iconName="RotateCcw">Limpar filtros</Button>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-[#f2e6d7] bg-[#fffaf4] p-4 text-sm leading-relaxed text-[#7a5c46]">
                <div className="mb-2 flex items-center gap-2 font-semibold text-[#3b1d0f]">
                  <Icon name="Link2" size={16} /> Como funciona o rastreio
                </div>
                <p>
                  Na venda, selecione NR Express, Minuano, Total Express ou Correios e clique em consultar. A NF, o CNPJ do emitente e o código são buscados no eGestor. Depois, confira o destinatário e envie o WhatsApp. Quando o site exigir verificação, abra o link oficial com os dados apresentados.
                </p>
                {manifestCompanies.length ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {manifestCompanies.map((company) => (
                      <span key={company.base} className="rounded-full border border-[#ead8c2] bg-white px-3 py-1 text-xs font-semibold text-[#6b4f3d]">
                        {formatBaseLabel(company.base)} · {company.nome || "Remetente"} · {formatCnpj(company.cnpj)}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>

              {manifestError ? <div className="mt-4 rounded-2xl border border-[#f8c9c9] bg-[#fff1f1] px-4 py-3 text-sm text-[#b42318]">{manifestError}</div> : null}
              {(manifest.warnings || []).length ? (
                <div className="mt-4 rounded-2xl border border-[#f7d69b] bg-[#fff8e8] px-4 py-3 text-sm text-[#8a5a00]">
                  Algumas vendas/empresas não retornaram detalhe: {(manifest.warnings || []).slice(0, 3).map((item) => `${formatBaseLabel(item.base, { emptyLabel: "Empresa" })}${item.codigo ? ` venda ${item.codigo}` : ""}: ${item.message}`).join(" | ")}
                </div>
              ) : null}
            </Panel>

            {manifestLoaded ? (
              <ExpeditionWorkflowPanel
                manifestSales={manifestSales}
                selectedManifestSales={selectedManifestSales}
                onOrdersChange={setExpeditionOrdersByKey}
                onSelectAllManifest={() => toggleAllManifestSales(true)}
                onClearManifestSelection={() => toggleAllManifestSales(false)}
                onPrintManifest={printSelectedManifestSales}
                onToggleManifestSale={(orderKey, checked) => toggleManifestSaleSelection({ id: orderKey }, checked)}
                printQuantities={manifestPrintQuantities}
                onUpdatePrintQuantity={updateManifestPrintQuantity}
                onMessageSent={() => setMessageHistoryRevision(value => value + 1)}
              />
            ) : null}

            <OrderMessageAutomationPanel historyRevision={messageHistoryRevision} />
          </div>
        ) : null}

        {activeTab === "combustivel" ? (
          <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-4 rounded-[24px] border border-[#ead8c2] bg-white p-5 shadow-sm md:flex-row md:items-end md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[#3b1d0f]">Competência de pagamento</h2>
                <p className="mt-1 text-sm text-[#7a5c46]">Escolha uma data para visualizar somente os lançamentos e cálculos daquele dia, ou limpe a data para consultar o mês inteiro.</p>
              </div>
              <div className="grid w-full gap-2 sm:w-auto sm:grid-cols-2 lg:flex lg:items-end">
                <Input
                  label="Data exata"
                  type="date"
                  value={fuelDate}
                  onChange={(event) => {
                    const value = event.target.value;
                    setFuelDate(value);
                    if (value) setFuelMonth(value.slice(0, 7));
                  }}
                  className="sm:min-w-[190px]"
                />
                <Input
                  label="Mês e ano"
                  type="month"
                  max={currentMonthISO()}
                  value={fuelMonth}
                  onChange={(event) => setFuelMonth(event.target.value || currentMonthISO())}
                  className="sm:min-w-[190px]"
                />
                <Button variant="outline" onClick={() => setFuelDate("")} iconName="CalendarRange">Ver mês inteiro</Button>
                {(fuelDate !== todayISO() || fuelMonth !== currentMonthISO()) ? <Button variant="outline" onClick={() => { setFuelDate(todayISO()); setFuelMonth(currentMonthISO()); }} iconName="CalendarDays">Hoje</Button> : null}
              </div>
            </section>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <MetricCard label="Cadastros ativos" value={formatNumber(combustivel.summary?.cadastrosAtivos || 0)} helper="Pilotos/motos" icon="Bike" />
              <MetricCard label="Lançamentos" value={formatNumber(combustivel.summary?.lancamentos || 0)} helper={combustivel.dateFilter ? formatDateBR(combustivel.dateFilter) : `Competência ${formatMonthBR(combustivel.monthKey)}`} icon="ClipboardList" />
              <MetricCard label="KM fora expediente" value={`${formatNumber(combustivel.summary?.kmForaExpediente || 0, 1)} km`} helper="Final do dia até início seguinte" icon="Gauge" />
              <MetricCard label="KM cobrados" value={`${formatNumber(combustivel.summary?.kmCobrado || 0, 1)} km`} helper="Descontando casa ↔ empresa no mês" icon="AlertTriangle" />
              <MetricCard label="Valor a cobrar" value={formatCurrency(combustivel.summary?.valorCobrado || 0)} helper="Total consolidado da competência" icon="Wallet" />
            </div>

            <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
              <Panel title="Cadastro do piloto e moto" subtitle="Nome, moto, placa e distância empresa ↔ casa" icon="Settings2">
                <div className="grid gap-4 lg:grid-cols-2">
                  <Input label="Nome do piloto" value={fuelVehicleForm.nome} onChange={(e) => setFuelVehicleForm((p) => ({ ...p, nome: e.target.value }))} />
                  <Input label="Modelo da moto" value={fuelVehicleForm.modeloMoto} onChange={(e) => setFuelVehicleForm((p) => ({ ...p, modeloMoto: e.target.value }))} />
                  <Input label="Placa" value={fuelVehicleForm.placa} onChange={(e) => setFuelVehicleForm((p) => ({ ...p, placa: e.target.value }))} />
                  <Input label="Empresa → casa (km)" type="number" step="0.01" value={fuelVehicleForm.distanciaEmpresaCasa} onChange={(e) => setFuelVehicleForm((p) => ({ ...p, distanciaEmpresaCasa: e.target.value }))} />
                  <Input label="Casa → empresa (km)" type="number" step="0.01" value={fuelVehicleForm.distanciaCasaEmpresa} onChange={(e) => setFuelVehicleForm((p) => ({ ...p, distanciaCasaEmpresa: e.target.value }))} />
                  <Input label="Preço gasolina" type="number" step="0.01" value={fuelVehicleForm.precoGasolina} onChange={(e) => setFuelVehicleForm((p) => ({ ...p, precoGasolina: e.target.value }))} />
                  <Input label="Consumo base (km por R$ gasolina)" type="number" step="0.01" value={fuelVehicleForm.consumoKmPorLitro} onChange={(e) => setFuelVehicleForm((p) => ({ ...p, consumoKmPorLitro: e.target.value }))} />
                  <label className="flex items-center gap-2 text-sm text-[#5d4637]"><input type="checkbox" checked={fuelVehicleForm.ativo} onChange={(e) => setFuelVehicleForm((p) => ({ ...p, ativo: e.target.checked }))} />Cadastro ativo</label>
                </div>
                <div className="mt-4 rounded-2xl border border-[#f2e6d7] bg-[#fffaf4] p-3 text-sm text-[#7a5c46]">
                  Exemplo: se terminou o dia com 2.000 km e voltou no dia seguinte com 2.100 km, rodou 100 km fora do expediente. Se a distância casa ↔ empresa for 20 km, cobra 80 km.
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button loading={saving} onClick={() => runAction(() => saveFuelVehicle(fuelVehicleForm), () => setFuelVehicleForm(initialFuelVehicleForm))} iconName="Save">{fuelVehicleForm.id ? "Atualizar cadastro" : "Cadastrar piloto/moto"}</Button>
                  {fuelVehicleForm.id ? <Button variant="outline" onClick={() => setFuelVehicleForm(initialFuelVehicleForm)}>Cancelar</Button> : null}
                </div>
              </Panel>

              <Panel title="Registro de KM" subtitle="Registre uma vez o KM inicial e uma vez o KM final" icon="Milestone">
                <div className="grid gap-4 lg:grid-cols-2">
                  <Input label="Data" type="date" value={fuelEntryForm.data} onChange={(e) => setFuelEntryForm((p) => ({ ...p, data: e.target.value }))} />
                  <Input label="Hora" type="time" value={fuelEntryForm.hora} onChange={(e) => setFuelEntryForm((p) => ({ ...p, hora: e.target.value }))} />
                  <Select label="Piloto / moto" options={fuelVehicleOptions} value={fuelEntryForm.vehicleId} onChange={(value) => setFuelEntryForm((p) => ({ ...p, vehicleId: value }))} placeholder="Selecione o cadastro" />
                  <Select label="Tipo" value={fuelEntryForm.tipo} onChange={(value) => setFuelEntryForm((p) => ({ ...p, tipo: value }))} options={[{ value: "inicio", label: "KM inicial" }, { value: "fim", label: "KM final" }]} />
                  <Input label="KM" type="number" step="0.01" value={fuelEntryForm.km} onChange={(e) => setFuelEntryForm((p) => ({ ...p, km: e.target.value }))} />
                  <Input label="Observação" value={fuelEntryForm.observacao} onChange={(e) => setFuelEntryForm((p) => ({ ...p, observacao: e.target.value }))} />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button loading={saving} onClick={() => runAction(() => saveFuelEntry(fuelEntryForm), () => setFuelEntryForm(initialFuelEntryForm))} iconName="Save">{fuelEntryForm.id ? "Atualizar KM" : "Salvar KM"}</Button>
                  {fuelEntryForm.id ? <Button variant="outline" onClick={() => setFuelEntryForm(initialFuelEntryForm)}>Cancelar</Button> : null}
                </div>
              </Panel>
            </div>

            <Panel title="Resumo geral do mês por piloto/moto" subtitle={`Total consolidado da competência ${formatMonthBR(combustivel.monthKey)} sem precisar somar a listagem manualmente`} icon="ClipboardList">
              <Table rows={fuelMonthlyByVehicle} columns={[
                { key: "nome", label: "Piloto" },
                { key: "modeloMoto", label: "Moto", render: (row) => `${row.modeloMoto || "---"}${row.placa ? ` · ${row.placa}` : ""}` },
                { key: "lancamentos", label: "Lançamentos", render: (row) => formatNumber(row.lancamentos) },
                { key: "paresCalculados", label: "Pares calculados", render: (row) => formatNumber(row.paresCalculados) },
                { key: "kmForaExpediente", label: "Rodado fora", render: (row) => `${formatNumber(row.kmForaExpediente, 1)} km` },
                { key: "kmDeslocamentoPermitido", label: "Desconto casa ↔ empresa", render: (row) => `${formatNumber(row.kmDeslocamentoPermitido, 1)} km` },
                { key: "kmCobrado", label: "KM cobrados", render: (row) => `${formatNumber(row.kmCobrado, 1)} km` },
                { key: "litrosCobrados", label: "Litros", render: (row) => `${formatNumber(row.litrosCobrados, 2)} L` },
                { key: "valorCobrado", label: "Valor", render: (row) => formatCurrency(row.valorCobrado) },
                { key: "ultimoFechamento", label: "Último cálculo", render: (row) => formatDateBR(row.ultimoFechamento) },
              ]} emptyMessage="Ainda não existe cálculo de combustível fechado nesta competência." />
            </Panel>

            <Panel title="Cobrança de combustível" subtitle={`Cálculos da competência ${formatMonthBR(combustivel.monthKey)}, descontando empresa ↔ casa`} icon="BarChartHorizontalBig">
              <Table rows={combustivel.fuelChargeSummaries || combustivel.dailySummaries || []} columns={[
                { key: "dataFim", label: "Último final", render: (row) => `${formatDateBR(row.dataFim)} ${row.horaFim || ""}` },
                { key: "dataInicio", label: "Próximo início", render: (row) => `${formatDateBR(row.dataInicio)} ${row.horaInicio || ""}` },
                { key: "nome", label: "Piloto" },
                { key: "modeloMoto", label: "Moto", render: (row) => `${row.modeloMoto || "---"}${row.placa ? ` · ${row.placa}` : ""}` },
                { key: "kmFinalExpediente", label: "KM final", render: (row) => formatNumber(row.kmFinalExpediente, 1) },
                { key: "kmInicialSeguinte", label: "KM inicial seguinte", render: (row) => formatNumber(row.kmInicialSeguinte, 1) },
                { key: "kmForaExpediente", label: "Rodado fora", render: (row) => `${formatNumber(row.kmForaExpediente, 1)} km` },
                { key: "kmDeslocamentoPermitido", label: "Desconto casa ↔ empresa", render: (row) => `${formatNumber(row.kmDeslocamentoPermitido, 1)} km` },
                { key: "kmCobrado", label: "KM cobrados", render: (row) => `${formatNumber(row.kmCobrado, 1)} km` },
                { key: "valorCobrado", label: "Valor", render: (row) => `${formatCurrency(row.valorCobrado)} (${formatNumber(row.kmCobrado, 1)} / ${formatNumber(row.consumoKmPorLitro, 1)} × ${formatCurrency(row.precoGasolina)})` },
                { key: "calculo", label: "Regra", render: () => "1 final → 1 início" },
              ]} emptyMessage="Ainda não existe sequência de último KM final + próximo KM inicial nesta competência." />
            </Panel>

            <div className="grid gap-6 xl:grid-cols-2">
              <Panel title="Cadastros de piloto/moto" subtitle="Base para cálculo de combustível" icon="Bike">
                <Table rows={snapshot.fuelVehicles || []} columns={[
                  { key: "nome", label: "Piloto" },
                  { key: "modeloMoto", label: "Moto" },
                  { key: "placa", label: "Placa" },
                  { key: "kmLivreDia", label: "Casa ↔ empresa", render: (row) => `${formatNumber(row.kmLivreDia, 1)} km` },
                  { key: "precoGasolina", label: "Gasolina", render: (row) => formatCurrency(row.precoGasolina) },
                  { key: "consumoKmPorLitro", label: "Base", render: (row) => `${formatNumber(row.consumoKmPorLitro, 1)} km` },
                  { key: "acoes", label: "Ações", render: (row) => <ActionButtons onEdit={() => setFuelVehicleForm({ ...initialFuelVehicleForm, ...row })} onDelete={() => handleDelete(deleteFuelVehicle, row.id)} /> },
                ]} />
              </Panel>

              <Panel title="Lançamentos de KM" subtitle={`Registros da competência ${formatMonthBR(combustivel.monthKey)}`} icon="History">
                <Table rows={snapshot.fuelEntries || []} columns={[
                  { key: "data", label: "Data", render: (row) => formatDateBR(row.data) },
                  { key: "hora", label: "Hora" },
                  { key: "nome", label: "Piloto" },
                  { key: "tipo", label: "Tipo", render: (row) => row.tipo === "inicio" ? "KM inicial" : "KM final" },
                  { key: "km", label: "KM", render: (row) => formatNumber(row.km, 1) },
                  { key: "observacao", label: "Obs." },
                  { key: "acoes", label: "Ações", render: (row) => <ActionButtons onEdit={() => setFuelEntryForm({ ...initialFuelEntryForm, ...row })} onDelete={() => handleDelete(deleteFuelEntry, row.id)} /> },
                ]} />
              </Panel>
            </div>
          </div>
        ) : null}

        {activeTab === "promotor" ? <IPDVModule /> : null}

        {false && activeTab === "promotor" ? (
          <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-4 rounded-[24px] border border-[#ead8c2] bg-white p-5 shadow-sm md:flex-row md:items-end md:justify-between">
              <div><h2 className="text-lg font-semibold text-[#3b1d0f]">Atividades do promotor por data</h2><p className="mt-1 text-sm text-[#7a5c46]">Os indicadores, lojas e visitas abaixo mostram somente o que foi realizado na data selecionada.</p></div>
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-end"><Input label="Data" type="date" value={promoterDate} onChange={(event) => setPromoterDate(event.target.value)} className="sm:min-w-[190px]" /><Button variant="outline" onClick={() => setPromoterDate(todayISO())} iconName="CalendarDays">Hoje</Button><Button variant="outline" onClick={() => setPromoterDate("")} iconName="CalendarRange">Todo o histórico</Button></div>
            </section>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <MetricCard label={promotor.dateFilter ? "Lojas visitadas" : "Lojas ativas"} value={formatNumber(promotor.summary?.lojasAtivas || 0)} helper={promotor.dateFilter ? formatDateBR(promotor.dateFilter) : "Visitadas nos últimos 30 dias"} icon="Store" />
              <MetricCard label={promotor.dateFilter ? "Visitas na data" : "Visitadas hoje"} value={formatNumber(promotor.summary?.totalVisitas || promotor.summary?.lojasVisitadasHoje || 0)} helper={promotor.dateFilter ? formatDateBR(promotor.dateFilter) : "Cobertura diária"} icon="CalendarCheck2" />
              <MetricCard label="Visitadas na semana" value={formatNumber(promotor.summary?.lojasVisitadasSemana || 0)} helper="Cobertura semanal" icon="CalendarRange" />
              <MetricCard label="Vendas calculadas" value={formatNumber(promotor.summary?.vendasCalculadas || 0)} helper="Diferença entre visitas" icon="TrendingUp" />
              <MetricCard label="Volume abastecido" value={formatNumber(promotor.summary?.volumeAbastecido || 0)} helper="Quantidade total listada" icon="Boxes" />
            </div>

            <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
              <Panel title="Cadastro de lojas" subtitle="CNPJ, loja, bairro, cidade e responsável" icon="Store">
                <div className="grid gap-4 lg:grid-cols-2">
                  <Input label="CNPJ" value={storeForm.cnpj} onChange={(e) => setStoreForm((p) => ({ ...p, cnpj: e.target.value }))} />
                  <Input label="Nome da loja" value={storeForm.nomeLoja} onChange={(e) => setStoreForm((p) => ({ ...p, nomeLoja: e.target.value }))} />
                  <Input label="Bairro" value={storeForm.bairro} onChange={(e) => setStoreForm((p) => ({ ...p, bairro: e.target.value }))} />
                  <Input label="Cidade" value={storeForm.cidade} onChange={(e) => setStoreForm((p) => ({ ...p, cidade: e.target.value }))} />
                  <Input label="Responsável" value={storeForm.responsavel} onChange={(e) => setStoreForm((p) => ({ ...p, responsavel: e.target.value }))} />
                  <label className="flex items-center gap-2 text-sm text-[#5d4637]"><input type="checkbox" checked={storeForm.ativo} onChange={(e) => setStoreForm((p) => ({ ...p, ativo: e.target.checked }))} />Loja ativa</label>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button loading={saving} onClick={() => runAction(() => savePromoterStore(storeForm), () => setStoreForm(initialStoreForm))} iconName="Save">{storeForm.id ? "Atualizar loja" : "Cadastrar loja"}</Button>
                  {storeForm.id ? <Button variant="outline" onClick={() => setStoreForm(initialStoreForm)}>Cancelar</Button> : null}
                </div>
              </Panel>

              <Panel title="Lançamento do promotor de vendas" subtitle="Quantidades por sabor, observações, entrega, checkout e checkstand" icon="ClipboardPen">
                <div className="grid gap-4 lg:grid-cols-2">
                  <Input label="Data" type="date" value={promoterForm.data} onChange={(e) => setPromoterForm((p) => ({ ...p, data: e.target.value }))} />
                  <Input label="Nome do colaborador" value={promoterForm.promoterName} onChange={(e) => setPromoterForm((p) => ({ ...p, promoterName: e.target.value }))} />
                  <div className="lg:col-span-2"><Select label="Loja" options={promoterStoreOptions} value={promoterForm.storeId} onChange={(value) => setPromoterForm((p) => ({ ...p, storeId: value }))} placeholder="Selecione a loja" /></div>
                </div>

                <div className="mt-4 max-h-[360px] overflow-y-auto pr-2">
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {(snapshot.productCatalog || []).map((product) => (
                      <Input key={product} label={product} type="number" value={promoterForm.quantities?.[product] || ""} onChange={(e) => setPromoterForm((p) => ({ ...p, quantities: { ...p.quantities, [product]: e.target.value } }))} />
                    ))}
                  </div>
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <Input label="Observações" value={promoterForm.observacao} onChange={(e) => setPromoterForm((p) => ({ ...p, observacao: e.target.value }))} />
                  <div className="grid gap-2 rounded-2xl border border-[#f2e6d7] bg-[#fffaf4] p-4 text-sm text-[#5d4637]">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={promoterForm.teveTroca} onChange={(e) => setPromoterForm((p) => ({ ...p, teveTroca: e.target.checked }))} /> Teve troca / avaria</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={promoterForm.entregaMercadoria} onChange={(e) => setPromoterForm((p) => ({ ...p, entregaMercadoria: e.target.checked }))} /> Houve entrega de mercadoria</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={promoterForm.displayCheckout} onChange={(e) => setPromoterForm((p) => ({ ...p, displayCheckout: e.target.checked }))} /> Display no checkout</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={promoterForm.displayCheckstand} onChange={(e) => setPromoterForm((p) => ({ ...p, displayCheckstand: e.target.checked }))} /> Display no checkstand</label>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button loading={saving} onClick={() => runAction(() => savePromoterRecord(promoterForm), () => setPromoterForm(resetPromoterForm(snapshot.productCatalog || [])))} iconName="Save">{promoterForm.id ? "Atualizar lançamento" : "Salvar lançamento"}</Button>
                  {promoterForm.id ? <Button variant="outline" onClick={() => setPromoterForm(resetPromoterForm(snapshot.productCatalog || []))}>Cancelar</Button> : null}
                </div>
              </Panel>
            </div>

            <Panel title="KPIs por loja" subtitle="Vendas calculadas pela diferença de produtos entre uma visita e outra" icon="PieChart">
              <Table rows={promoterStoreStatsWithGap || []} columns={[
                { key: "nomeLoja", label: "Loja" },
                { key: "cidade", label: "Cidade" },
                { key: "totalVisitas", label: "Visitas", render: (row) => formatNumber(row.totalVisitas) },
                { key: "totalVolume", label: "Volume abastecido", render: (row) => formatNumber(row.totalVolume) },
                { key: "vendaUltimaVisita", label: "Venda última visita", render: (row) => formatNumber(row.vendaUltimaVisita) },
                { key: "giroMedio", label: "Giro médio", render: (row) => formatNumber(row.giroMedio, 1) },
                { key: "penultimaVisita", label: "Penúltima visita", render: (row) => formatDateBR(row.penultimaVisita) },
                { key: "ultimaVisita", label: "Última visita", render: (row) => formatDateBR(row.ultimaVisita) },
                { key: "diferencaVisitasDias", label: "Diferença visitas", render: (row) => row.diferencaVisitasDias === null || row.diferencaVisitasDias === undefined ? "---" : `${formatNumber(row.diferencaVisitasDias)} dias` },
                { key: "diasSemVisita", label: "Dias sem visita", render: (row) => row.diasSemVisita === null || row.diasSemVisita === undefined ? "---" : `${formatNumber(row.diasSemVisita)} dias` },
                { key: "statusTroca", label: "Situação", render: (row) => row.teveTroca ? <span className="inline-flex rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">Crítico · troca pendente</span> : <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">Em dia</span> },
              ]} emptyMessage="Nenhuma loja cadastrada ainda." />
            </Panel>

            <div className="grid gap-6 xl:grid-cols-2">
              <Panel title="Lojas cadastradas" subtitle="Base configurável do promotor de vendas" icon="Building2">
                <PaginatedTable pageSize={6} rows={snapshot.promoterStores || []} columns={[
                  { key: "cnpj", label: "CNPJ" },
                  { key: "nomeLoja", label: "Loja" },
                  { key: "bairro", label: "Bairro" },
                  { key: "cidade", label: "Cidade" },
                  { key: "responsavel", label: "Responsável" },
                  { key: "acoes", label: "Ações", render: (row) => <ActionButtons onEdit={() => setStoreForm({ ...initialStoreForm, ...row })} onDelete={() => handleDelete(deletePromoterStore, row.id)} /> },
                ]} />
              </Panel>

              <Panel title="Visitas lançadas" subtitle="Histórico do promotor com produtos e observações" icon="History">
                <PaginatedTable pageSize={6} rows={snapshot.promoterRecords || []} columns={[
                  { key: "data", label: "Data", render: (row) => formatDateBR(row.data) },
                  { key: "promoterName", label: "Colaborador" },
                  { key: "storeName", label: "Loja" },
                  { key: "quantities", label: "Qtd. total", render: (row) => formatNumber(quantityTotal(row.quantities)) },
                  { key: "entregaMercadoria", label: "Entrega", render: (row) => row.entregaMercadoria ? "Sim" : "Não" },
                  { key: "teveTroca", label: "Troca", render: (row) => row.teveTroca ? "Sim" : "Não" },
                  { key: "statusTroca", label: "Conclusão da troca", render: (row) => !row.teveTroca ? "Não houve" : row.trocaResolvida ? <div><div className="font-semibold text-emerald-700">Realizada</div><div className="mt-1 text-xs text-[#8b6a52]">{row.trocaResolvidaPor || "Responsável não informado"} · {formatDateTimeBR(row.trocaResolvidaEm)}</div></div> : <span className="font-semibold text-red-700">Pendente / crítico</span> },
                  { key: "acoes", label: "Ações", render: (row) => <ActionButtons onEdit={() => setPromoterForm({ ...resetPromoterForm(snapshot.productCatalog || []), ...row, quantities: { ...buildEmptyQuantities(snapshot.productCatalog || []), ...(row.quantities || {}) } })} onDelete={() => handleDelete(deletePromoterRecord, row.id)} /> },
                ]} />
              </Panel>
            </div>

            <div className="grid gap-6 xl:grid-cols-4">
              <Panel title="Alerta: lojas com troca" subtitle="Acompanhamento de avaria e substituição" icon="AlertCircle">
                <ul className="max-h-[280px] space-y-3 overflow-y-auto pr-2 text-sm text-[#5d4637]">
                  {(promotor.alerts?.lojasComTroca || []).length === 0 ? <li>Nenhuma loja com troca registrada.</li> : null}
                  {(promotor.alerts?.lojasComTroca || []).map((item) => <li key={item.storeId} className="rounded-xl border border-red-200 bg-red-50 px-3 py-3"><div className="flex items-start justify-between gap-3"><div><div className="font-semibold text-red-800">{item.nomeLoja}</div><div className="mt-0.5 text-xs text-red-700">Crítico · troca informada em {formatDateBR(item.trocaRegistradaEm)}</div><div className="text-xs text-red-700">{item.cidade || ""}</div></div><button type="button" disabled={saving} onClick={() => handleResolveReplacement(item)} className="shrink-0 rounded-lg border border-red-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-50">Confirmar troca</button></div></li>)}
                </ul>
              </Panel>
              <Panel title="Alerta: 7 dias sem visita" subtitle="Lojas sem visita há pelo menos 7 dias" icon="Clock3">
                <ul className="max-h-[280px] space-y-3 overflow-y-auto pr-2 text-sm text-[#5d4637]">
                  {(promotor.alerts?.lojasSemVisita7Dias || []).length === 0 ? <li>Todas as lojas tiveram visita nos últimos 7 dias.</li> : null}
                  {(promotor.alerts?.lojasSemVisita7Dias || []).map((item) => <li key={item.storeId} className="rounded-xl border border-[#f2e6d7] bg-[#fffaf4] px-3 py-2"><div className="font-semibold text-[#3b1d0f]">{item.nomeLoja}</div><div>Última visita: {formatDateBR(item.ultimaVisita)}</div></li>)}
                </ul>
              </Panel>
              <Panel title="Alerta: 15 dias sem visita" subtitle="Lojas sem visita há pelo menos 15 dias" icon="Clock3">
                <ul className="max-h-[280px] space-y-3 overflow-y-auto pr-2 text-sm text-[#5d4637]">
                  {(promotor.alerts?.lojasSemVisita15Dias || []).length === 0 ? <li>Nenhuma loja passou de 15 dias sem visita.</li> : null}
                  {(promotor.alerts?.lojasSemVisita15Dias || []).map((item) => <li key={item.storeId} className="rounded-xl border border-[#f2e6d7] bg-[#fffaf4] px-3 py-2"><div className="font-semibold text-[#3b1d0f]">{item.nomeLoja}</div><div>Última visita: {formatDateBR(item.ultimaVisita)}</div></li>)}
                </ul>
              </Panel>
              <Panel title="Alerta: fora do checkout/checkstand" subtitle="Lojas sem exposição informada" icon="LayoutGrid">
                <ul className="max-h-[280px] space-y-3 overflow-y-auto pr-2 text-sm text-[#5d4637]">
                  {(promotor.alerts?.lojasForaCheckout || []).length === 0 ? <li>Sem alertas de exposição no momento.</li> : null}
                  {(promotor.alerts?.lojasForaCheckout || []).map((item) => <li key={item.storeId} className="rounded-xl border border-[#f2e6d7] bg-[#fffaf4] px-3 py-2"><div className="font-semibold text-[#3b1d0f]">{item.nomeLoja}</div><div>{item.bairro || ""} {item.cidade ? `· ${item.cidade}` : ""}</div></li>)}
                </ul>
              </Panel>
            </div>
          </div>
        ) : null}
      </div>
    </PageShell>
  );
}
